/* ============================================================
   Classy Kitchen — Premium Product Page
   assets/premium-product.js
   Vanilla JS only. No jQuery. No frameworks.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Boot once per section instance ---------- */
  document.querySelectorAll('.tan-product').forEach(initProductSection);

  function initProductSection(root) {
    if (root.dataset.ckInit) return;
    root.dataset.ckInit = 'true';

    let productData = {};
    try {
      productData = JSON.parse(root.dataset.productJson || '{}');
    } catch (e) {
      productData = {};
    }

    initGallery(root);
    initVariantMediaMap(root);
    initWishlist(root);
    initVariantPicker(root, productData);
    initVariantCards(root, productData);
    initQuantityStepper(root);
    initAddToCart(root);
    initStickyBar(root);
    initTabs(root);
    initMobileAccordion(root);
    initFaq(root);
    initPincodeChecker(root);
    initRipple(root);
    initFadeInObserver(root);
    initRelatedSliders(root);
    initRecentlyViewed(root, productData);
    initFrequentlyBoughtTogether(root);
  }

  /* ============================================================
     GALLERY: thumbnails, prev/next, counter, zoom, lazy load
     ============================================================ */
  function initGallery(root) {
    const stage = root.querySelector('[data-tan-stage]');
    if (!stage) return;

    const slides = Array.from(stage.querySelectorAll('[data-tan-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-tan-thumb]'));
    const prevBtn = root.querySelector('[data-tan-prev]');
    const nextBtn = root.querySelector('[data-tan-next]');
    const currentEl = root.querySelector('[data-tan-current]');
    let index = 0;

    function show(i) {
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      index = i;

      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === index));
      thumbs.forEach((t, idx) => {
        t.classList.toggle('is-active', idx === index);
        t.setAttribute('aria-selected', idx === index ? 'true' : 'false');
      });
      if (currentEl) currentEl.textContent = index + 1;

      // pause any videos not visible
      slides.forEach((s, idx) => {
        const video = s.querySelector('video');
        if (video && idx !== index) video.pause();
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(parseInt(thumb.dataset.index, 10)));
    });
    if (prevBtn) prevBtn.addEventListener('click', () => show(index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => show(index + 1));

    // keyboard support
    stage.setAttribute('tabindex', '0');
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    // swipe support
    let touchStartX = 0;
    stage.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(diff) > 40) show(diff > 0 ? index - 1 : index + 1);
    }, { passive: true });

    // hover zoom (desktop): track pointer position for a focal zoom
    stage.querySelectorAll('[data-tan-zoom]').forEach((zoomEl) => {
      const img = zoomEl.querySelector('img');
      if (!img) return;
      zoomEl.addEventListener('mousemove', (e) => {
        const rect = zoomEl.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      zoomEl.addEventListener('mouseleave', () => {
        img.style.transformOrigin = 'center center';
      });
    });

    // Expose a way for the variant picker / variant cards to switch
    // the gallery to whichever slide matches a variant's own image,
    // without duplicating the show()/thumb-sync logic here. Tries
    // the media id first — this is now built from
    // variant.featured_media.id (the same id space product.media
    // uses, matching Shopify's own Dawn/Horizon gallery approach) —
    // then falls back to a filename match for extra safety.
    root._ckGallerySlideToMedia = function (mediaId, mediaFilename) {
      let target = -1;
      if (mediaId) {
        target = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      }
      if (target === -1 && mediaFilename) {
        target = slides.findIndex((s) => s.dataset.mediaFilename && s.dataset.mediaFilename === mediaFilename);
      }
      if (target > -1) {
        show(target);
      } else if (window.console && console.warn) {
        // Diagnostic only — never blocks the UI. If this fires, the
        // variant's image genuinely isn't present among the gallery
        // slides (or isn't assigned in Shopify Admin under
        // Variants → [variant] → Image), so no JS matching strategy
        // can find it. Check console for the values that failed to
        // match against each slide's data-media-filename/data-media-id.
        console.warn('[CK gallery] No slide matched variant image.', { mediaId, mediaFilename, availableSlides: slides.map((s) => ({ id: s.dataset.mediaId, filename: s.dataset.mediaFilename })) });
      }
    };
  }

  /* ============================================================
     VARIANT → MEDIA MAP — parses the Liquid-built JSON map
     (data-tan-variant-media-map) into root._ckVariantMediaMap.
     This is the reliable source for "which gallery image belongs
     to this variant", since it's built directly from Liquid
     variant objects rather than the nested product|json blob,
     which can drop variant.featured_image on deep serialization.
     ============================================================ */
  function initVariantMediaMap(root) {
    root._ckVariantMediaMap = {};
    const mapScript = root.querySelector('[data-tan-variant-media-map]');
    if (!mapScript) return;
    try {
      root._ckVariantMediaMap = JSON.parse(mapScript.textContent.trim() || '{}');
    } catch (e) {
      root._ckVariantMediaMap = {};
    }
  }

  /* ============================================================
     WISHLIST
     ============================================================ */
  function initWishlist(root) {
    const btn = root.querySelector('[data-tan-wishlist]');
    if (!btn) return;
    const handle = root.dataset.productHandle;
    const key = 'ck_wishlist';

    function getWishlist() {
      try { return JSON.parse(localStorage.getItem(key) || '[]'); }
      catch (e) { return []; }
    }
    function setActive() {
      const list = getWishlist();
      btn.classList.toggle('is-active', list.includes(handle));
    }
    setActive();

    btn.addEventListener('click', () => {
      let list = getWishlist();
      if (list.includes(handle)) {
        list = list.filter((h) => h !== handle);
      } else {
        list.push(handle);
      }
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
      setActive();
    });
  }

  /* ============================================================
     MONEY FORMAT + VARIANT UI SYNC — shared by the pill picker
     and the variant cards, so both apply the same full update
     (price, compare price, discount %, stock, button state,
     and gallery image) instead of only touching the price text.
     ============================================================ */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '₹' + amount.replace(/\.00$/, '');
  }

  function applyVariantToUI(root, variant) {
    if (!variant) return;

    const hiddenIdInput = root.querySelector('[data-tan-variant-id]');
    const nativeSelect = root.querySelector('[data-tan-native-select]');
    const priceEl = root.querySelector('[data-tan-price]');
    const compareEl = root.querySelector('[data-tan-compare-price]');
    const discountEl = root.querySelector('[data-tan-discount-badge]');
    const stockEl = root.querySelector('[data-tan-stock-status]');
    const addBtn = root.querySelector('[data-tan-add-to-cart]');
    const addLabel = root.querySelector('[data-tan-add-label]');
    const buyBtn = root.querySelector('[data-tan-buy-now]');
    const stickyPrice = root.querySelector('[data-tan-sticky-price]');

    if (hiddenIdInput) hiddenIdInput.value = variant.id;
    if (nativeSelect) nativeSelect.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      }
      if (discountEl) {
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        discountEl.textContent = pct + '% OFF';
        discountEl.hidden = false;
      }
    } else {
      const fallbackPct = parseFloat(root.dataset.fallbackDiscount) || 0;
      if (fallbackPct > 0) {
        const syntheticCompareAt = Math.round(variant.price / (1 - fallbackPct / 100));
        if (compareEl) {
          compareEl.textContent = formatMoney(syntheticCompareAt);
          compareEl.hidden = false;
        }
        if (discountEl) {
          discountEl.textContent = fallbackPct + '% OFF';
          discountEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (discountEl) discountEl.hidden = true;
      }
    }

    if (stockEl) {
      stockEl.innerHTML = variant.available
        ? '<span class="tan-dot tan-dot--in"></span> In stock, ready to ship'
        : '<span class="tan-dot tan-dot--out"></span> Currently out of stock';
    }

    [addBtn, buyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = !variant.available;
    });
    if (addLabel) addLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

    // Swap the gallery to this variant's own image, if it has one.
    // root._ckVariantMediaMap (built directly in Liquid) gives us
    // {m: mediaId, f: filename} per variant, where mediaId comes
    // from variant.featured_media.id — the SAME id space product.media
    // uses (this is what Shopify's own Dawn/Horizon gallery relies on
    // internally). variant.featured_image.id lives in a different,
    // older id space and was the real reason matching kept failing.
    // Filename is kept as a secondary fallback. Variants with no
    // dedicated image (e.g. a Size-only variant on a product where
    // only Color has images) correctly leave the gallery untouched.
    if (typeof root._ckGallerySlideToMedia === 'function') {
      const mapEntry = root._ckVariantMediaMap && root._ckVariantMediaMap[String(variant.id)];
      const mappedMediaId = mapEntry && mapEntry.m;
      const mappedFilename = mapEntry && mapEntry.f;
      const fallbackId = variant.featured_image && variant.featured_image.id;
      const mediaId = mappedMediaId || fallbackId;
      if (mediaId || mappedFilename) {
        root._ckGallerySlideToMedia(mediaId, mappedFilename);
      }
    }
  }

  /* ============================================================
     VARIANT PICKER
     ============================================================ */
  function initVariantPicker(root, productData) {
    const optionSelectors = Array.from(root.querySelectorAll('[data-tan-option-selector]'));
    if (!optionSelectors.length) return;

    const variants = productData.variants || [];
    const selections = {};

    // seed current selections from the "is-selected" pill in each option group
    root.querySelectorAll('[data-tan-option]').forEach((group) => {
      const pos = group.dataset.optionPosition;
      const selected = group.querySelector('.is-selected');
      if (selected) selections[pos] = selected.dataset.value;
    });

    function findVariant() {
      return variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return Object.keys(selections).every((pos) => {
          const idx = parseInt(pos, 10) - 1;
          return opts[idx] === selections[pos];
        });
      });
    }

    function updateUI(variant) {
      applyVariantToUI(root, variant);

      // update option value labels (specific to the pill picker)
      root.querySelectorAll('[data-tan-option]').forEach((group) => {
        const pos = group.dataset.optionPosition;
        const valueEl = group.querySelector('[data-tan-option-value]');
        if (valueEl && selections[pos]) valueEl.textContent = selections[pos];
      });
    }

    optionSelectors.forEach((pill) => {
      pill.addEventListener('click', () => {
        const group = pill.closest('[data-tan-option]');
        const pos = pill.dataset.optionPosition;

        group.querySelectorAll('[data-tan-option-selector]').forEach((p) => {
          p.classList.remove('is-selected');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('is-selected');
        pill.setAttribute('aria-checked', 'true');

        selections[pos] = pill.dataset.value;
        const variant = findVariant();
        if (variant) updateUI(variant);
      });
    });
  }

  /* ============================================================
     VARIANT CARDS (photo + price) — the picker actually shown
     on the page. Full UI sync on click: price, compare price,
     discount badge, stock status, gallery image, and Add to
     Cart/Buy Now disabled state, not just the price text.
     ============================================================ */
  function initVariantCards(root, productData) {
    const wrap = root.querySelector('[data-tan-variant-cards]');
    if (!wrap) return;

    const variants = productData.variants || [];

    wrap.addEventListener('click', (e) => {
      const card = e.target.closest('[data-tan-variant-card]');
      if (!card || card.disabled) return;

      wrap.querySelectorAll('[data-tan-variant-card]').forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');

      const variantId = card.dataset.variantId;
      const variant = variants.find((v) => String(v.id) === String(variantId));

      if (variant) {
        applyVariantToUI(root, variant);
      } else {
        // Fallback if productData wasn't available for some reason —
        // at least keep the variant id and price in sync, formatted
        // client-side (never from a pre-rendered money-filter string).
        const hiddenInput = root.querySelector('[data-tan-variant-id]');
        if (hiddenInput) hiddenInput.value = variantId;
        const priceEl = root.querySelector('[data-tan-price]');
        if (priceEl && card.dataset.variantPrice) {
          priceEl.textContent = formatMoney(parseInt(card.dataset.variantPrice, 10));
        }
      }
    });
  }

  /* ============================================================
     QUANTITY STEPPER
     ============================================================ */
  function initQuantityStepper(root) {
    const input = root.querySelector('[data-tan-qty-input]');
    const minus = root.querySelector('[data-tan-qty-minus]');
    const plus = root.querySelector('[data-tan-qty-plus]');
    if (!input) return;

    minus && minus.addEventListener('click', () => {
      const val = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.value = val;
    });
    plus && plus.addEventListener('click', () => {
      const val = (parseInt(input.value, 10) || 1) + 1;
      input.value = val;
    });
    input.addEventListener('change', () => {
      if (!input.value || parseInt(input.value, 10) < 1) input.value = 1;
    });
  }

  /* ============================================================
     SHARED TOAST — one small, self-contained confirmation banner
     used for cart feedback (and reusable anywhere else later).
     Appended to <body> once, reused for every call.
     ============================================================ */
  function ckShowToast(message, isError) {
    let toast = document.querySelector('.tan-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'tan-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.toggle('tan-toast--error', !!isError);

    // restart the animation/visibility cleanly even on rapid repeats
    toast.classList.remove('is-visible');
    // eslint-disable-next-line no-unused-expressions
    void toast.offsetWidth; // force reflow so the class re-add re-triggers CSS transition
    toast.classList.add('is-visible');

    window.clearTimeout(toast._ckHideTimer);
    toast._ckHideTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2600);
  }

  /* ============================================================
     OPEN CART DRAWER — triggers whichever cart UI the installed
     cart/checkout app (e.g. Shopflo) already provides, after a
     successful Add to Cart, instead of leaving the customer with
     only a toast.

     Primary method: Shopify's standard storefront actions API
     (window.Shopify.actions.openCart()) — the current, theme-
     and-app-agnostic way for any custom code to say "show the
     cart" without needing to know if that means a drawer, a
     popup, or a page. Modern cart apps are expected to configure
     this action to open their own UI.

     Fallback: if that API isn't present (older app version),
     click whatever cart-open trigger the site already has wired
     up — the same element a customer would click manually. This
     never fights with the primary method; only one runs per call.
     ============================================================ */
  function ckOpenCartDrawer() {
    try {
      if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        window.Shopify.actions.openCart();
        return;
      }
    } catch (e) {
      // fall through to the fallback below
    }

    var candidateSelectors = [
      '[data-cart-icon]',
      '[data-cart-drawer-toggle]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '.cart-icon',
      'a[href="/cart"]',
      'button[aria-controls*="cart" i]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = document.querySelector(candidateSelectors[i]);
      if (el) {
        el.click();
        return;
      }
    }
  }

  /* ============================================================
     GO TO CHECKOUT — used by Buy Now. Checkout accelerator apps
     like Shopflo work by injecting scripts that intercept clicks
     on the theme's REAL checkout button(s) and swap in their own
     accelerated flow. A raw `window.location.href = '/checkout'`
     is a scripted navigation, not a click — there's nothing for
     that injected script to intercept, so it lands on plain
     native Shopify checkout instead of Shopflo.

     Fix: open the cart (same trigger Shopflo already hooks for
     its "view cart → initiate checkout" flow), give it a moment
     to render, then look for a real Checkout button/link inside
     it and click that. Only if no such element can be found does
     this fall back to a direct /checkout navigation, so behavior
     never breaks — it just won't be Shopflo-accelerated in that
     edge case (e.g. Shopflo temporarily uninstalled).

     `onSettled` (added) fires once the hand-off is fully resolved:
     either a real checkout button was clicked/we gave up and hard-
     navigated (in which case the page is now leaving and this
     callback's caller no longer matters), OR — the case this
     actually guards against — nothing navigated at all (e.g. a
     checkout app that opens an in-page overlay instead of a new
     page). In that second case the caller uses this signal to
     release the button lock instead of leaving Buy Now stuck
     disabled forever with no way to recover.
     ============================================================ */
  function ckClickCheckoutButton() {
    var candidateSelectors = [
      'form[action^="/cart"] button[name="checkout"]',
      'button[name="checkout"]',
      '#CartDrawer-Checkout',
      '.cart__checkout-button',
      '[data-shopflo-checkout]',
      'a[href="/checkout"]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = document.querySelector(candidateSelectors[i]);
      if (el && el.offsetParent !== null) {
        el.click();
        return true;
      }
    }
    // Fallback: any visible button/link whose visible text is exactly
    // "Checkout" (catches Shopflo's own drawer button even without
    // knowing its exact class names in advance).
    var all = document.querySelectorAll('button, a');
    for (var j = 0; j < all.length; j++) {
      var text = (all[j].textContent || '').trim().toLowerCase();
      if (text === 'checkout' && all[j].offsetParent !== null) {
        all[j].click();
        return true;
      }
    }
    return false;
  }

  // FIX: a single check after a fixed 600ms delay was too fragile — on a
  // cold load, Shopflo's drawer can take noticeably longer than that to
  // render its Checkout button, so we were searching too early, finding
  // nothing, and falling back to plain native Shopify checkout even
  // though Shopflo genuinely was about to be ready. Polling repeatedly
  // for up to 4 seconds (instead of checking once) catches it whenever
  // it actually appears, however long that takes within a reasonable
  // ceiling, and still degrades gracefully to /checkout if it never
  // shows up at all.
  function ckGoToCheckout(onSettled) {
    ckOpenCartDrawer();

    var maxWaitMs = 4000;
    var intervalMs = 150;
    var waited = 0;
    var settleGraceMs = 600; // time to let a real click/redirect actually start unloading the page

    var poll = window.setInterval(() => {
      var clicked = ckClickCheckoutButton();
      waited += intervalMs;
      if (clicked || waited >= maxWaitMs) {
        window.clearInterval(poll);
        if (!clicked) {
          window.location.href = '/checkout';
        }
        // If a real navigation is happening, this callback never actually
        // runs — the page unloads before the timeout fires, which is the
        // whole point of the delay. It only fires (and matters) when
        // nothing actually left the page.
        window.setTimeout(() => {
          if (typeof onSettled === 'function') onSettled();
        }, settleGraceMs);
      }
    }, intervalMs);
  }

  /* ============================================================
     ADD TO CART / BUY NOW  (Shopify AJAX Cart API)
     Every trigger (main Add to Cart, main Buy Now, sticky Add,
     sticky Buy) now gives the customer clear feedback: the button
     shows "Adding…" while the request is in flight, is disabled
     to prevent accidental double-adds, then shows "Added ✓" (or
     stays "Redirecting…" for Buy Now, since the page navigates
     away) on success, plus a toast confirmation. On failure the
     button resets and an error toast explains what happened,
     instead of failing silently.

     Two extra guarantees added on top of that:
     - The pair of buttons NOT being used (e.g. Add to Cart while
       Buy Now is mid-flight) is disabled for the duration too, so
       a click during that window can't silently no-op — every
       click either does something or is visibly unclickable.
     - `isSubmitting` stays true for the ENTIRE Buy Now → checkout
       hand-off (which can take up to ~4s while Shopflo's checkout
       UI renders), not just the initial /cart/add.js call. Without
       this, a second click during that window could fire a second,
       overlapping cart-add request right before checkout.
     ============================================================ */
  function initAddToCart(root) {
    const form = root.querySelector('#tan-product-form') || root.querySelector('.tan-form');
    const addBtn = root.querySelector('[data-tan-add-to-cart]');
    const buyBtn = root.querySelector('[data-tan-buy-now]');
    const stickyAddBtn = root.querySelector('[data-tan-sticky-add]');
    const stickyBuyBtn = root.querySelector('[data-tan-sticky-buy]');

    let isSubmitting = false;

    function getFormData() {
      const idInput = root.querySelector('[data-tan-variant-id]');
      const qtyInput = root.querySelector('[data-tan-qty-input]');
      return {
        id: idInput ? idInput.value : null,
        quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
      };
    }

    // Captures each button's original inner markup once, so it can be
    // restored exactly (icons, arrow spans, etc.) after a loading state.
    function setBusy(btn, label) {
      if (!btn) return;
      if (btn.dataset.ckOriginalHtml === undefined) {
        btn.dataset.ckOriginalHtml = btn.innerHTML;
      }
      btn.disabled = true;
      btn.classList.add('is-loading');
      const textEl = btn.querySelector('.tan-btn-text') || btn.querySelector('[data-tan-add-label]');
      if (textEl) {
        textEl.textContent = label;
      } else {
        btn.textContent = label;
      }
    }

    function setDone(btn, label, revertAfterMs) {
      if (!btn) return;
      const textEl = btn.querySelector('.tan-btn-text') || btn.querySelector('[data-tan-add-label]');
      if (textEl) {
        textEl.textContent = label;
      } else {
        btn.textContent = label;
      }
      window.setTimeout(() => {
        resetButton(btn);
      }, revertAfterMs || 1600);
    }

    function resetButton(btn) {
      if (!btn) return;
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (btn.dataset.ckOriginalHtml !== undefined) {
        btn.innerHTML = btn.dataset.ckOriginalHtml;
      }
    }

    // Only flips disabled state — never touches label/markup — so it's
    // safe to call on a button whose text is being independently managed
    // by setBusy/setDone elsewhere without clobbering that text.
    function setDisabledOnly(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
    }

    // FIX: previously this set the SAME label ("Adding…" / "Redirecting…")
    // on ALL FOUR buttons regardless of which one was actually clicked —
    // so clicking Buy Now made the Add to Cart button falsely say
    // "Redirecting…" too. Each action still only relabels its own matching
    // pair (Add to Cart + sticky Add, or Buy Now + sticky Buy). The OTHER
    // pair is now explicitly disabled (not relabeled) for the duration,
    // so a click on it is visibly blocked instead of silently swallowed
    // by the isSubmitting guard with no feedback.
    function setActionBusy(redirectToCheckout, label) {
      if (redirectToCheckout) {
        setBusy(buyBtn, label);
        setBusy(stickyBuyBtn, label);
        setDisabledOnly(addBtn, true);
        setDisabledOnly(stickyAddBtn, true);
      } else {
        setBusy(addBtn, label);
        setBusy(stickyAddBtn, label);
        setDisabledOnly(buyBtn, true);
        setDisabledOnly(stickyBuyBtn, true);
      }
    }

    // Fully resets every button back to its normal, clickable state —
    // used on failure, and on bfcache restore. (The success path for a
    // normal Add to Cart uses setDone's own delayed reset for the acting
    // pair, plus an immediate unlock of the other pair — see below.)
    function resetActionButtons() {
      resetButton(addBtn);
      resetButton(stickyAddBtn);
      resetButton(buyBtn);
      resetButton(stickyBuyBtn);
    }

    function addToCart(redirectToCheckout) {
      if (isSubmitting) return; // guards against double taps mid-request
      const data = getFormData();
      if (!data.id) return;

      isSubmitting = true;
      setActionBusy(redirectToCheckout, 'Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      })
        .then((res) => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(() => {
          if (redirectToCheckout) {
            // FIX: isSubmitting used to be released here, immediately —
            // but the checkout hand-off below can still be in progress
            // for up to ~4 seconds (Shopflo's UI needs time to render).
            // Releasing the lock early reopened the exact double-add race
            // this guard exists to prevent. It now stays held until
            // ckGoToCheckout's onSettled fires, which only happens once
            // the hand-off is truly done (or the page has already
            // navigated away, in which case none of this JS runs again
            // anyway).
            setActionBusy(redirectToCheckout, 'Redirecting…');
            ckGoToCheckout(() => {
              // Only reached if the page never actually left — e.g. the
              // checkout app opened an overlay instead of navigating.
              // Release everything instead of leaving Buy Now stuck.
              isSubmitting = false;
              resetActionButtons();
            });
          } else {
            isSubmitting = false;
            [addBtn, stickyAddBtn].forEach((b) => setDone(b, 'Added ✓'));
            // The Add to Cart pair is mid-request no more — unlock the
            // Buy Now pair immediately rather than waiting on setDone's
            // delayed revert, which only applies to the acting pair.
            setDisabledOnly(buyBtn, false);
            setDisabledOnly(stickyBuyBtn, false);
            ckShowToast('Added to cart');
            ckOpenCartDrawer();
            document.dispatchEvent(new CustomEvent('ck:cart:added', { detail: data }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        })
        .catch(() => {
          isSubmitting = false;
          resetActionButtons();
          ckShowToast("Couldn't add to cart. Please try again.", true);
          // Fallback: submit the native form so Shopify handles it server-side
          if (!redirectToCheckout && form) form.submit();
        });
    }

    // The form submit listener stays as a safety net (covers cases where
    // the button genuinely is inside the form, or Enter is pressed in a
    // field), but the DIRECT click listener below is what actually makes
    // "Add to Cart" reliable: this button is type="submit" but sits
    // outside <form id="tan-product-form"> in the rendered markup, and a
    // submit button with no form ancestor (and no form="..." attribute)
    // does nothing when clicked — no request, no error, just silence.
    // Buy Now / sticky Add / sticky Buy never had this problem because
    // they were already wired with direct click listeners. (If a future
    // markup change ever does put addBtn inside the form, both this and
    // the click listener firing for one click is still safe: isSubmitting
    // is set synchronously by whichever handler runs first, so the
    // second handler's guard check blocks it before any second request
    // goes out.)
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        addToCart(false);
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(false);
      });
    }

    if (buyBtn) buyBtn.addEventListener('click', () => addToCart(true));
    if (stickyAddBtn) stickyAddBtn.addEventListener('click', () => addToCart(false));
    if (stickyBuyBtn) stickyBuyBtn.addEventListener('click', () => addToCart(true));

    // FIX: pressing the browser's Back button after Buy Now navigated to
    // checkout can restore this page from the back-forward cache (bfcache)
    // instead of reloading it — meaning our JS does NOT re-run, and the
    // Buy Now button stays permanently stuck showing "Redirecting…" from
    // right before the navigation away. The pageshow event's `persisted`
    // flag is exactly how a bfcache restore is detected; when it fires,
    // put every cart button back to its normal, clickable state.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        isSubmitting = false;
        resetActionButtons();
      }
    });
  }

  /* ============================================================
     STICKY ADD TO CART BAR — Intersection Observer, no scroll spam
     ============================================================ */
  function initStickyBar(root) {
    const bar = root.querySelector('[data-tan-sticky-bar]');
    const hero = root.querySelector('.tan-hero');
    if (!bar || !hero) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.hidden = false;
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0, rootMargin: '0px' });

    observer.observe(hero);
  }

  /* ============================================================
     TABS
     ============================================================ */
  function initTabs(root) {
    const nav = root.querySelector('[data-tan-tabs-nav]');
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('[data-tan-tab-btn]'));
    const panels = Array.from(root.querySelectorAll('[data-tan-tab-panel]'));

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.ckTabBtn;

        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.ckTabPanel === target));
      });
    });
  }


function initMobileAccordion(root) {

    // Desktop: do nothing
    if (window.innerWidth > 749) return;

    const items = root.querySelectorAll(".tan-mobile-item");

    if (!items.length) return;

    items.forEach((item) => {

        const button = item.querySelector(".tan-mobile-btn");

        if (!button) return;

        button.addEventListener("click", function () {

            const isOpen = item.classList.contains("active");

            items.forEach(i => i.classList.remove("active"));

            if (!isOpen) {
                item.classList.add("active");
            }

        });

    });

}

  /* ============================================================
     FAQ ACCORDION — only one open at a time
     ============================================================ */
  function initFaq(root) {
    const items = Array.from(root.querySelectorAll('[data-tan-faq-item]'));
    if (!items.length) return;

    items.forEach((item) => {
      const question = item.querySelector('[data-tan-faq-question]');
      const answer = item.querySelector('[data-tan-faq-answer]');
      if (!question || !answer) return;

      question.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        items.forEach((other) => {
          other.classList.remove('is-open');
          const otherAnswer = other.querySelector('[data-tan-faq-answer]');
          if (otherAnswer) otherAnswer.style.maxHeight = null;
          const otherQ = other.querySelector('[data-tan-faq-question]');
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
          question.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }


  /* ============================================================
     DELIVERY PINCODE CHECKER — custom delivery days per pincode,
     driven by a merchant-editable JSON prefix map in the schema
     (section.settings.pincode_delivery_map, rendered into a
     <script type="application/json" data-tan-pincode-map> tag by
     the .liquid file). Longest matching prefix wins (checks
     6-digit down to 1-digit), falling back to
     section.settings.default_delivery_days when nothing matches.
     ============================================================ */
  function initPincodeChecker(root) {
    const wrap = root.querySelector('.tan-delivery-checker');
    const btn = root.querySelector('[data-tan-check-pincode]');
    const input = root.querySelector('[data-tan-pincode]');
    const result = root.querySelector('[data-tan-delivery-result]');
    if (!btn || !input || !result) return;

    const defaultDays = parseInt((wrap && wrap.dataset.defaultDays), 10) || 5;

    let pincodeMap = {};
    const mapScript = root.querySelector('[data-tan-pincode-map]');
    if (mapScript) {
      try {
        pincodeMap = JSON.parse(mapScript.textContent.trim() || '{}');
      } catch (e) {
        pincodeMap = {};
      }
    }

    function getDeliveryDays(pincode) {
      // Check longest prefix first so more specific entries
      // (e.g. "400" for Mumbai) win over broader ones (e.g. "4").
      for (let len = 6; len >= 1; len--) {
        const prefix = pincode.slice(0, len);
        if (Object.prototype.hasOwnProperty.call(pincodeMap, prefix)) {
          return pincodeMap[prefix];
        }
      }
      return defaultDays;
    }

    function formatEta(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }

    function checkPincode() {
      const val = input.value.trim();

      if (!/^\d{6}$/.test(val)) {
        result.hidden = false;
        result.classList.add('is-error');
        result.textContent = 'Please enter a valid 6-digit pincode.';
        return;
      }

      const days = getDeliveryDays(val);
      const eta = formatEta(days);

      result.hidden = false;
      result.classList.remove('is-error');
      result.textContent = days <= 1
        ? 'Delivery by tomorrow, ' + eta
        : 'Delivery in ' + days + ' days, by ' + eta;
    }

    btn.addEventListener('click', checkPincode);

    // keep the field numeric-only, max 6 digits, as the user types
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        checkPincode();
      }
    });
  }

  /* ============================================================
     BUTTON RIPPLE EFFECT
     ============================================================ */
  function initRipple(root) {
    root.querySelectorAll('.tan-ripple').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        const rect = btn.getBoundingClientRect();
        const circle = document.createElement('span');
        const size = Math.max(rect.width, rect.height);
        circle.className = 'tan-ripple-circle';
        circle.style.width = circle.style.height = size + 'px';
        circle.style.left = (e.clientX - rect.left - size / 2) + 'px';
        circle.style.top = (e.clientY - rect.top - size / 2) + 'px';
        btn.appendChild(circle);
        window.setTimeout(() => circle.remove(), 650);
      });
    });
  }

  /* ============================================================
     FADE-IN ON SCROLL — single shared IntersectionObserver
     ============================================================ */
  function initFadeInObserver(root) {
    const targets = root.querySelectorAll('.tan-fade-in');
    if (!targets.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    targets.forEach((t) => observer.observe(t));

  }

  /* ============================================================
     RELATED / RECENTLY VIEWED SLIDER CONTROLS
     ============================================================ */
  function initRelatedSliders(root) {
    root.querySelectorAll('[data-tan-slider]').forEach((slider) => {
      const track = slider.querySelector('.tan-slider-track');
      const prev = slider.parentElement.querySelector('[data-tan-slider-prev]');
      const next = slider.parentElement.querySelector('[data-tan-slider-next]');
      if (!track) return;

      function scrollByAmount(dir) {
        const card = track.querySelector('.tan-product-card');
        const amount = card ? card.getBoundingClientRect().width + 20 : 280;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', () => scrollByAmount(-1));
      if (next) next.addEventListener('click', () => scrollByAmount(1));
    });

    // Quick add buttons on product cards
    root.querySelectorAll('[data-tan-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then((res) => res.json())
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            ckOpenCartDrawer();
            const label = btn.textContent;
            btn.textContent = 'Added ✓';
            window.setTimeout(() => { btn.textContent = label; }, 1600);
          })
          .catch(() => {});
      });
    });

    // Wishlist buttons on product cards
    root.querySelectorAll('[data-tan-card-wishlist]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.classList.toggle('is-active');
      });
    });
  }

  /* ============================================================
     CURRENCY FORMAT HELPER — shared by Recently Viewed + FBT
     ============================================================ */
  function formatCurrency(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '₹' + Math.round(amount);
    }
  }

  /* ============================================================
     RECENTLY VIEWED — localStorage-backed, matches the
     Related Products card style (.tan-rv-*), with a working
     persistent Add to Cart button per card.
     ============================================================ */
  function initRecentlyViewed(root, productData) {
    const key = 'ck_recently_viewed';
    const handle = root.dataset.productHandle;
    if (!handle) return;

    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }

    list = list.filter((h) => h !== handle);
    list.unshift(handle);
    list = list.slice(0, 12);

    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }

    const section = root.querySelector('[data-tan-recently-viewed]');
    const track = root.querySelector('[data-tan-recently-viewed-track]');
    const others = list.filter((h) => h !== handle).slice(0, 8);

    if (!section || !track || others.length === 0) return;

    const run = window.requestIdleCallback || window.setTimeout;
    run(() => {
      Promise.all(
        others.map((h) =>
          fetch('/products/' + h + '.js')
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        products.filter(Boolean).forEach((p) => {
          track.appendChild(buildRecentlyViewedCard(p));
        });
        if (track.children.length) section.hidden = false;
      });
    });

    // Delegated add-to-cart for the persistent button on each card
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tan-rv-add]');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      e.preventDefault();

      const original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then((res) => res.json())
        .then(() => {
          btn.textContent = 'Added ✓';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          ckOpenCartDrawer();
          window.setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 1600);
        })
        .catch(() => {
          btn.textContent = original;
          btn.disabled = false;
        });
    });
  }

  function buildRecentlyViewedCard(product) {
    const rawImg = product.featured_image || (product.images && product.images[0]) || '';
    const img = rawImg ? (rawImg.indexOf('http') === 0 ? rawImg : 'https:' + rawImg) : '';

    const price = product.price / 100;
    const compareAt = product.compare_at_price ? product.compare_at_price / 100 : null;
    const hasDiscount = compareAt && compareAt > price;
    const savePct = hasDiscount ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

    let variant = (product.variants || []).find((v) => v.available);
    if (!variant && product.variants && product.variants.length) variant = product.variants[0];

    const card = document.createElement('div');
    card.className = 'tan-rv-card';

    const media = document.createElement('div');
    media.className = 'tan-rv-media';

    if (hasDiscount) {
      const badge = document.createElement('span');
      badge.className = 'tan-rv-badge';
      badge.textContent = savePct + '% OFF';
      media.appendChild(badge);
    }

    const wishlistBtn = document.createElement('button');
    wishlistBtn.type = 'button';
    wishlistBtn.className = 'tan-rv-wishlist';
    wishlistBtn.setAttribute('data-tan-card-wishlist', '');
    wishlistBtn.setAttribute('aria-label', 'Add to wishlist');
    wishlistBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 21s-7.5-4.6-10-9.2C.4 8.1 2.1 4 6 4c2.1 0 3.6 1.1 4.5 2.4C11.4 5.1 12.9 4 15 4c3.9 0 5.6 4.1 4 7.8-2.5 4.6-10 9.2-10 9.2Z"/></svg>';
    media.appendChild(wishlistBtn);

    const mediaLink = document.createElement('a');
    mediaLink.href = '/products/' + product.handle;
    mediaLink.className = 'tan-rv-media-link';
    if (img) {
      const imgEl = document.createElement('img');
      imgEl.src = img;
      imgEl.alt = product.title || '';
      imgEl.loading = 'lazy';
      imgEl.width = 260;
      imgEl.height = 260;
      mediaLink.appendChild(imgEl);
    }
    media.appendChild(mediaLink);
    card.appendChild(media);

    const body = document.createElement('div');
    body.className = 'tan-rv-body';

    const titleLink = document.createElement('a');
    titleLink.href = '/products/' + product.handle;
    titleLink.className = 'tan-rv-title';
    titleLink.textContent = product.title;
    body.appendChild(titleLink);

    const priceRow = document.createElement('div');
    priceRow.className = 'tan-rv-price-row';
    const priceNow = document.createElement('span');
    priceNow.className = 'tan-rv-price-now';
    priceNow.textContent = formatCurrency(price);
    priceRow.appendChild(priceNow);
    if (hasDiscount) {
      const priceWas = document.createElement('span');
      priceWas.className = 'tan-rv-price-was';
      priceWas.textContent = formatCurrency(compareAt);
      priceRow.appendChild(priceWas);
    }
    body.appendChild(priceRow);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'tan-rv-add';
    if (variant && variant.available) {
      addBtn.textContent = 'Add to Cart';
      addBtn.setAttribute('data-tan-rv-add', '');
      addBtn.dataset.variantId = variant.id;
    } else {
      addBtn.textContent = 'View Product';
      addBtn.addEventListener('click', () => {
        window.location.href = '/products/' + product.handle;
      });
    }
    body.appendChild(addBtn);

    card.appendChild(body);
    return card;
  }

  /* ============================================================
     FREQUENTLY BOUGHT TOGETHER — live total + single multi-item
     add to cart. Anchor product is always included.
     ============================================================ */
  function initFrequentlyBoughtTogether(root) {
    const fbt = root.querySelector('[data-tan-fbt]');
    if (!fbt) return;

    const checkboxes = Array.from(fbt.querySelectorAll('[data-tan-fbt-checkbox]'));
    const totalEl = fbt.querySelector('[data-tan-fbt-total]');
    const countEl = fbt.querySelector('[data-tan-fbt-item-count]');
    const addBtn = fbt.querySelector('[data-tan-fbt-add]');

    function recalc() {
      let total = 0;
      let count = 0;
      checkboxes.forEach((cb) => {
        if (cb.checked) {
          const item = cb.closest('[data-tan-fbt-item]');
          total += parseFloat(item.dataset.price) || 0;
          count++;
        }
      });
      if (totalEl) totalEl.textContent = formatCurrency(total / 100);
      if (countEl) countEl.textContent = count;
    }

    checkboxes.forEach((cb) => cb.addEventListener('change', recalc));

    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const items = [];
        checkboxes.forEach((cb) => {
          if (!cb.checked) return;
          const variantId = cb.dataset.variantId;
          if (variantId) items.push({ id: variantId, quantity: 1 });
        });
        const anchorInput = root.querySelector('[data-tan-variant-id]');
        if (anchorInput && anchorInput.value) items.unshift({ id: anchorInput.value, quantity: 1 });
        if (!items.length) return;

        const original = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        })
          .then((res) => res.json())
          .then(() => {
            addBtn.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            ckOpenCartDrawer();
            window.setTimeout(() => {
              addBtn.textContent = original;
              addBtn.disabled = false;
            }, 1600);
          })
          .catch(() => {
            addBtn.textContent = original;
            addBtn.disabled = false;
          });
      });
    }

    recalc();
  }
})();
/* ============================================================
   AMAZON-STYLE PRODUCT ZOOM  (add-on for assets/premium-product.js)
   Paste this whole block at the VERY END of premium-product.js,
   after the last  })();

   Desktop (mouse, ≥ 990px wide)
     • Hover the main image → a lens follows the cursor and a large
       zoom window opens beside the image (like Amazon).
     • If there isn't room beside the image, the zoom shows inside
       the image instead.
     • Click the image → full-screen viewer.
   Mobile / touch
     • Tap the image → full-screen viewer.
     • Pinch to zoom, double-tap to zoom in/out, drag to move,
       swipe left/right to change photo.
   Full-screen viewer (all devices)
     • Mouse wheel / + − buttons zoom, click toggles zoom,
       arrows + thumbnails change photo, Esc closes.
     • Changing photo in the viewer also changes the page gallery.

   Uses a sharper copy of each photo (2400px, via Shopify's CDN
   `width` parameter). Put data-zoom-src="…" on an <img> to use a
   specific zoom image instead. Nothing else in the file changes.
   ============================================================ */
(function () {
  'use strict';

  var ZOOM = 2.5;      // lens / hover-window magnification
  var MAX_ZOOM = 4;    // max zoom in the full-screen viewer
  var HIRES = 2400;    // width of the sharp image used for zooming

  injectStyles();

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  ready(function () { document.querySelectorAll('.tan-product').forEach(initZoom); });
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('.tan-product').forEach(initZoom);
  });

  /* ---------------- helpers ---------------- */
  function clamp(v, a, b) { return Math.min(Math.max(v, a), b); }

  function canHoverZoom() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth >= 990;
  }

  function hiRes(img, width) {
    var src = img.getAttribute('data-zoom-src') || img.currentSrc || img.src || '';
    if (!src) return '';
    try {
      var u = new URL(src, location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) {
        u.pathname = u.pathname.replace(/_(\d+x\d*|\d*x\d+)(@\dx)?(?=\.[a-z]+$)/i, '');
        u.searchParams.set('width', width || HIRES);
        u.searchParams.delete('height');
        u.searchParams.delete('crop');
      }
      return u.href;
    } catch (e) {
      return src;
    }
  }

  // The part of the <img> box actually covered by the photo (handles object-fit).
  function drawnBox(img) {
    var r = img.getBoundingClientRect();
    var nw = img.naturalWidth || r.width;
    var nh = img.naturalHeight || r.height;
    var fit = window.getComputedStyle(img).objectFit;
    var w = r.width, h = r.height, x = r.left, y = r.top, sc;
    if (fit === 'contain' || fit === 'scale-down') {
      sc = Math.min(r.width / nw, r.height / nh);
      if (fit === 'scale-down') sc = Math.min(sc, 1);
      w = nw * sc; h = nh * sc;
      x = r.left + (r.width - w) / 2; y = r.top + (r.height - h) / 2;
    } else if (fit === 'cover') {
      sc = Math.max(r.width / nw, r.height / nh);
      w = nw * sc; h = nh * sc;
      x = r.left + (r.width - w) / 2; y = r.top + (r.height - h) / 2;
    }
    return { x: x, y: y, w: w, h: h };
  }

  /* ---------------- per product ---------------- */
  function initZoom(root) {
    if (root.dataset.tanZoomInit) return;
    var stage = root.querySelector('[data-tan-stage]');
    if (!stage) return;
    root.dataset.tanZoomInit = 'true';

    var slides = Array.prototype.slice.call(stage.querySelectorAll('[data-tan-slide]'));
    var items = []; // { slideIndex, host, img }
    slides.forEach(function (slide, i) {
      if (slide.querySelector('video, iframe, model-viewer')) return;
      var host = slide.querySelector('[data-tan-zoom]') || slide;
      var img = host.querySelector('img');
      if (img) items.push({ slideIndex: i, host: host, img: img });
    });
    if (!items.length) return;

    var ctx = { root: root, stage: stage, slides: slides, items: items };
    ctx.pane = document.createElement('div');
    ctx.pane.className = 'tan-zm-pane';
    ctx.pane.setAttribute('aria-hidden', 'true');
    document.body.appendChild(ctx.pane);

    ctx.viewer = buildViewer(ctx);

    items.forEach(function (item, itemIndex) { bindImage(ctx, item, itemIndex); });

    // Hint under the gallery, like Amazon
    if (!root.querySelector('.tan-zm-hint')) {
      var hint = document.createElement('p');
      hint.className = 'tan-zm-hint';
      hint.innerHTML =
        '<span class="tan-zm-hint--mouse">Roll over image to zoom in · Click to open expanded view</span>' +
        '<span class="tan-zm-hint--touch">Tap image to zoom</span>';
      stage.insertAdjacentElement('afterend', hint);
    }

    window.addEventListener('scroll', function () { hidePane(ctx); }, { passive: true });
  }

  function hidePane(ctx) {
    ctx.pane.style.display = 'none';
    if (ctx.activeLens) ctx.activeLens.style.display = 'none';
  }

  /* ---------------- desktop hover lens + zoom window ---------------- */
  function bindImage(ctx, item, itemIndex) {
    var host = item.host, img = item.img;
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.classList.add('tan-zm-host');

    var lens = document.createElement('div');
    lens.className = 'tan-zm-lens';
    lens.setAttribute('aria-hidden', 'true');
    host.appendChild(lens);

    var active = false;

    host.addEventListener('mouseenter', function (e) {
      if (!canHoverZoom()) return;
      active = true;
      ctx.activeLens = lens;
      var big = hiRes(img);
      var small = img.currentSrc || img.src;
      ctx.pane.style.backgroundImage = 'url("' + big + '"), url("' + small + '")';
      move(e);
    });

    host.addEventListener('mousemove', function (e) { if (active) move(e); });

    host.addEventListener('mouseleave', function () {
      active = false;
      hidePane(ctx);
    });

    host.addEventListener('click', function (e) {
      if (e.target.closest('a, button') && !e.target.closest('.tan-zm-host > a')) return;
      e.preventDefault();
      active = false;
      hidePane(ctx);
      openViewer(ctx, itemIndex, host);
    });

    function move(e) {
      if (!canHoverZoom()) { hidePane(ctx); return; }
      var d = drawnBox(img);
      var hb = host.getBoundingClientRect();
      var vis = {
        l: Math.max(d.x, hb.left), t: Math.max(d.y, hb.top),
        r: Math.min(d.x + d.w, hb.right), b: Math.min(d.y + d.h, hb.bottom)
      };
      if (e.clientX < vis.l || e.clientX > vis.r || e.clientY < vis.t || e.clientY > vis.b) {
        hidePane(ctx);
        return;
      }

      // Zoom window beside the image; inside the image if there's no room.
      var gap = 20;
      var pl = hb.right + gap;
      var pt = Math.max(12, hb.top);
      var pw = Math.min(hb.width * 1.15, window.innerWidth - pl - 16);
      var ph = Math.min(hb.height, window.innerHeight - pt - 12);
      var inside = pw < 280 || ph < 200;
      if (inside) { pl = hb.left; pt = hb.top; pw = hb.width; ph = hb.height; }

      var pane = ctx.pane;
      pane.classList.toggle('is-inside', inside);
      pane.style.left = pl + 'px';
      pane.style.top = pt + 'px';
      pane.style.width = pw + 'px';
      pane.style.height = ph + 'px';
      pane.style.display = 'block';

      var z = ZOOM;
      var lw = Math.min(pw / z, vis.r - vis.l);
      var lh = Math.min(ph / z, vis.b - vis.t);
      var lx = clamp(e.clientX - lw / 2, vis.l, vis.r - lw);
      var ly = clamp(e.clientY - lh / 2, vis.t, vis.b - lh);

      pane.style.backgroundSize = (d.w * z) + 'px ' + (d.h * z) + 'px';
      pane.style.backgroundPosition = (-(lx - d.x) * z) + 'px ' + (-(ly - d.y) * z) + 'px';

      if (inside) {
        lens.style.display = 'none';
      } else {
        lens.style.display = 'block';
        lens.style.left = (lx - hb.left) + 'px';
        lens.style.top = (ly - hb.top) + 'px';
        lens.style.width = lw + 'px';
        lens.style.height = lh + 'px';
      }
    }
  }

  /* ---------------- full-screen viewer ---------------- */
  function buildViewer(ctx) {
    var v = document.createElement('div');
    v.className = 'tan-zm-lb';
    v.setAttribute('role', 'dialog');
    v.setAttribute('aria-modal', 'true');
    v.setAttribute('aria-label', 'Product images');
    v.hidden = true;
    v.innerHTML =
      '<div class="tan-zm-lb__bar">' +
        '<span class="tan-zm-lb__count" data-zm-count></span>' +
        '<div class="tan-zm-lb__tools">' +
          '<button type="button" class="tan-zm-lb__tool" data-zm-out aria-label="Zoom out">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" stroke="currentColor" stroke-width="1.8" fill="none"/></svg></button>' +
          '<button type="button" class="tan-zm-lb__tool" data-zm-in aria-label="Zoom in">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5v14" stroke="currentColor" stroke-width="1.8" fill="none"/></svg></button>' +
          '<button type="button" class="tan-zm-lb__tool" data-zm-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" fill="none"/></svg></button>' +
        '</div>' +
      '</div>' +
      '<div class="tan-zm-lb__stage" data-zm-stage>' +
        '<img class="tan-zm-lb__img" data-zm-img alt="" draggable="false">' +
        '<button type="button" class="tan-zm-lb__nav tan-zm-lb__nav--prev" data-zm-prev aria-label="Previous image">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 6-6 6 6 6" stroke="currentColor" stroke-width="1.8" fill="none"/></svg></button>' +
        '<button type="button" class="tan-zm-lb__nav tan-zm-lb__nav--next" data-zm-next aria-label="Next image">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="1.8" fill="none"/></svg></button>' +
      '</div>' +
      '<div class="tan-zm-lb__thumbs" data-zm-thumbs role="list"></div>';
    document.body.appendChild(v);

    var thumbsWrap = v.querySelector('[data-zm-thumbs]');
    ctx.items.forEach(function (item, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tan-zm-lb__thumb';
      b.setAttribute('role', 'listitem');
      b.setAttribute('aria-label', 'Image ' + (i + 1));
      b.dataset.zmThumb = i;
      b.innerHTML = '<img src="' + hiRes(item.img, 160) + '" alt="" loading="lazy">';
      thumbsWrap.appendChild(b);
    });
    if (ctx.items.length < 2) v.classList.add('is-single');

    var st = { i: 0, s: 1, x: 0, y: 0 };
    ctx.st = st;
    var stageEl = v.querySelector('[data-zm-stage]');
    var imgEl = v.querySelector('[data-zm-img]');
    ctx.vStage = stageEl;
    ctx.vImg = imgEl;

    v.addEventListener('click', function (e) {
      if (e.target.closest('[data-zm-close]')) { closeViewer(ctx); return; }
      if (e.target.closest('[data-zm-prev]')) { goTo(ctx, st.i - 1); return; }
      if (e.target.closest('[data-zm-next]')) { goTo(ctx, st.i + 1); return; }
      if (e.target.closest('[data-zm-in]')) { zoomAt(ctx, st.s * 1.5, 0, 0, true); return; }
      if (e.target.closest('[data-zm-out]')) { zoomAt(ctx, st.s / 1.5, 0, 0, true); return; }
      var t = e.target.closest('[data-zm-thumb]');
      if (t) goTo(ctx, Number(t.dataset.zmThumb));
    });

    v.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); closeViewer(ctx); }
      else if (e.key === 'ArrowLeft' && st.s === 1) { e.preventDefault(); goTo(ctx, st.i - 1); }
      else if (e.key === 'ArrowRight' && st.s === 1) { e.preventDefault(); goTo(ctx, st.i + 1); }
      else if (e.key === '+' || e.key === '=') { zoomAt(ctx, st.s * 1.5, 0, 0, true); }
      else if (e.key === '-') { zoomAt(ctx, st.s / 1.5, 0, 0, true); }
      else if (e.key === 'Tab') {
        var f = Array.prototype.slice.call(v.querySelectorAll('button')).filter(function (b) { return b.offsetParent !== null; });
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });

    stageEl.addEventListener('wheel', function (e) {
      e.preventDefault();
      var p = relToCenter(ctx, e.clientX, e.clientY);
      zoomAt(ctx, st.s * (e.deltaY < 0 ? 1.18 : 1 / 1.18), p.x, p.y, false);
    }, { passive: false });

    bindGestures(ctx);
    window.addEventListener('resize', function () { if (!v.hidden) { clampPan(ctx); apply(ctx, false); } });
    return v;
  }

  function relToCenter(ctx, cx, cy) {
    var r = ctx.vStage.getBoundingClientRect();
    return { x: cx - (r.left + r.width / 2), y: cy - (r.top + r.height / 2) };
  }

  // Size of the photo as drawn (object-fit: contain) at scale 1.
  function contentSize(ctx) {
    var W = ctx.vStage.clientWidth, H = ctx.vStage.clientHeight;
    var nw = ctx.vImg.naturalWidth || W, nh = ctx.vImg.naturalHeight || H;
    var sc = Math.min(W / nw, H / nh);
    return { w: nw * sc, h: nh * sc, W: W, H: H };
  }

  function clampPan(ctx) {
    var st = ctx.st, c = contentSize(ctx);
    var mx = Math.max(0, (c.w * st.s - c.W) / 2);
    var my = Math.max(0, (c.h * st.s - c.H) / 2);
    st.x = clamp(st.x, -mx, mx);
    st.y = clamp(st.y, -my, my);
  }

  function apply(ctx, animate) {
    var st = ctx.st;
    ctx.vImg.style.transition = animate ? 'transform .25s ease' : 'none';
    ctx.vImg.style.transform = 'translate(' + st.x + 'px,' + st.y + 'px) scale(' + st.s + ')';
    ctx.viewer.classList.toggle('is-zoomed', st.s > 1.01);
  }

  function zoomAt(ctx, ns, px, py, animate) {
    var st = ctx.st;
    ns = clamp(ns, 1, MAX_ZOOM);
    st.x = px - (px - st.x) * (ns / st.s);
    st.y = py - (py - st.y) * (ns / st.s);
    st.s = ns;
    if (ns === 1) { st.x = 0; st.y = 0; }
    clampPan(ctx);
    apply(ctx, animate);
  }

  function bindGestures(ctx) {
    var stageEl = ctx.vStage, st = ctx.st;
    var pts = new Map();
    var start = null, moved = false, lastTap = { t: 0, x: 0, y: 0 };

    function list() { return Array.from(pts.values()); }
    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

    function begin() {
      var p = list();
      start = { s: st.s, x: st.x, y: st.y, p0: p[0] ? { x: p[0].x, y: p[0].y } : null };
      if (p.length === 2) {
        start.dist = dist(p[0], p[1]) || 1;
        start.mid = relToCenter(ctx, (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
      }
    }

    stageEl.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button')) return;
      stageEl.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 1) moved = false;
      begin();
    });

    stageEl.addEventListener('pointermove', function (e) {
      if (!pts.has(e.pointerId) || !start) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var p = list();

      if (p.length === 2 && start.dist) {
        moved = true;
        var ns = clamp(start.s * dist(p[0], p[1]) / start.dist, 1, MAX_ZOOM);
        var mid = relToCenter(ctx, (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
        st.x = mid.x - (start.mid.x - start.x) * (ns / start.s);
        st.y = mid.y - (start.mid.y - start.y) * (ns / start.s);
        st.s = ns;
        clampPan(ctx);
        apply(ctx, false);
        return;
      }

      if (p.length === 1 && start.p0) {
        var dx = p[0].x - start.p0.x, dy = p[0].y - start.p0.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        if (!moved) return;
        if (st.s > 1.01) {
          st.x = start.x + dx; st.y = start.y + dy;
          clampPan(ctx);
        } else {
          st.x = dx * 0.6; st.y = 0; // swipe preview
        }
        apply(ctx, false);
      }
    });

    function end(e) {
      if (!pts.has(e.pointerId)) return;
      var last = pts.get(e.pointerId);
      pts.delete(e.pointerId);

      if (pts.size > 0) { begin(); return; }

      if (st.s <= 1.01 && start && start.p0 && moved) {
        var dx = last.x - start.p0.x, dy = last.y - start.p0.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
          goTo(ctx, st.i + (dx < 0 ? 1 : -1));
        } else {
          st.x = 0; st.y = 0; apply(ctx, true);
        }
      }

      if (!moved && e.type === 'pointerup') {
        var p = relToCenter(ctx, last.x, last.y);
        if (e.pointerType === 'mouse') {
          zoomAt(ctx, st.s > 1.01 ? 1 : ZOOM, p.x, p.y, true);
        } else {
          var now = Date.now();
          if (now - lastTap.t < 320 && Math.hypot(last.x - lastTap.x, last.y - lastTap.y) < 30) {
            zoomAt(ctx, st.s > 1.01 ? 1 : ZOOM, p.x, p.y, true);
            lastTap = { t: 0, x: 0, y: 0 };
          } else {
            lastTap = { t: now, x: last.x, y: last.y };
          }
        }
      }
      start = null;
    }
    stageEl.addEventListener('pointerup', end);
    stageEl.addEventListener('pointercancel', end);
  }

  function loadImage(ctx, index) {
    var item = ctx.items[index];
    var imgEl = ctx.vImg;
    imgEl.src = item.img.currentSrc || item.img.src;
    imgEl.alt = item.img.alt || '';
    var big = hiRes(item.img);
    var pre = new Image();
    pre.onload = function () { if (ctx.st.i === index) imgEl.src = big; };
    pre.src = big;
  }

  function syncGallery(ctx, index) {
    var item = ctx.items[index];
    var slide = ctx.slides[item.slideIndex];
    if (typeof ctx.root._ckGallerySlideToMedia === 'function' && (slide.dataset.mediaId || slide.dataset.mediaFilename)) {
      ctx.root._ckGallerySlideToMedia(slide.dataset.mediaId, slide.dataset.mediaFilename);
      return;
    }
    var thumb = ctx.root.querySelector('[data-tan-thumb][data-index="' + item.slideIndex + '"]');
    if (thumb) thumb.click();
  }

  function goTo(ctx, index) {
    var n = ctx.items.length;
    index = ((index % n) + n) % n;
    var st = ctx.st;
    st.i = index; st.s = 1; st.x = 0; st.y = 0;
    apply(ctx, false);
    loadImage(ctx, index);
    var v = ctx.viewer;
    v.querySelector('[data-zm-count]').textContent = (index + 1) + ' / ' + n;
    v.querySelectorAll('[data-zm-thumb]').forEach(function (b, i) {
      b.classList.toggle('is-active', i === index);
      if (i === index) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
    var active = v.querySelector('[data-zm-thumb].is-active');
    if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'center' });
    syncGallery(ctx, index);
  }

  function openViewer(ctx, index, opener) {
    ctx.opener = opener || document.activeElement;
    ctx.viewer.hidden = false;
    document.documentElement.classList.add('tan-zm-lock');
    goTo(ctx, index);
    requestAnimationFrame(function () {
      ctx.viewer.classList.add('is-open');
      var c = ctx.viewer.querySelector('[data-zm-close]');
      if (c) c.focus({ preventScroll: true });
    });
  }

  function closeViewer(ctx) {
    ctx.viewer.classList.remove('is-open');
    document.documentElement.classList.remove('tan-zm-lock');
    setTimeout(function () { ctx.viewer.hidden = true; }, 200);
    if (ctx.opener && document.contains(ctx.opener)) {
      if (!ctx.opener.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT)$/.test(ctx.opener.tagName)) ctx.opener.setAttribute('tabindex', '-1');
      ctx.opener.focus({ preventScroll: true });
    }
  }

  /* ---------------- styles (injected once) ---------------- */
  function injectStyles() {
    if (document.getElementById('tan-zoom-styles')) return;
    var css = [
      /* turn off the old scale-on-hover so it can't fight the lens */
      '.tan-product [data-tan-zoom] img,.tan-zm-host img{transform:none!important}',
      '.tan-zm-host{cursor:zoom-in}',
      '@media (hover:hover) and (pointer:fine) and (min-width:990px){.tan-zm-host{cursor:crosshair}}',
      '.tan-zm-lens{position:absolute;z-index:5;display:none;pointer-events:none;',
        'background-color:rgb(255 255 255 / .28);',
        'background-image:radial-gradient(rgb(91 50 24 / .35) 1px,transparent 1px);background-size:6px 6px;',
        'border:1px solid rgb(91 50 24 / .55);box-shadow:0 2px 10px rgb(0 0 0 / .12)}',
      '.tan-zm-pane{position:fixed;z-index:1000;display:none;pointer-events:none;',
        'background-color:#fff;background-repeat:no-repeat;',
        'border:1px solid #E8DCCB;border-radius:4px;box-shadow:0 18px 50px rgb(40 22 10 / .18)}',
      '.tan-zm-pane.is-inside{border:0;border-radius:0;box-shadow:none}',
      '.tan-zm-hint{margin:10px 0 0;font-size:12px;line-height:1.4;text-align:center;color:inherit;opacity:.65}',
      '.tan-zm-hint--mouse{display:none}',
      '@media (hover:hover) and (pointer:fine) and (min-width:990px){.tan-zm-hint--mouse{display:inline}.tan-zm-hint--touch{display:none}}',

      'html.tan-zm-lock,html.tan-zm-lock body{overflow:hidden}',
      '.tan-zm-lb{position:fixed;inset:0;z-index:10000;display:flex;flex-direction:column;background:#fff;color:#2A1C13;opacity:0;transition:opacity .2s ease}',
      '.tan-zm-lb.is-open{opacity:1}',
      '.tan-zm-lb[hidden]{display:none}',
      '.tan-zm-lb__bar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px;border-bottom:1px solid #E8DCCB}',
      '.tan-zm-lb__count{font-size:13px;letter-spacing:.06em}',
      '.tan-zm-lb__tools{display:flex;gap:6px}',
      '.tan-zm-lb__tool{display:grid;place-items:center;width:40px;height:40px;border:1px solid #E8DCCB;border-radius:50%;background:#fff;color:inherit;cursor:pointer}',
      '.tan-zm-lb__tool:hover{border-color:#5B3218;color:#5B3218}',
      '.tan-zm-lb__tool svg,.tan-zm-lb__nav svg{width:18px;height:18px}',
      '.tan-zm-lb__stage{position:relative;flex:1;min-height:0;overflow:hidden;touch-action:none;cursor:zoom-in;user-select:none;-webkit-user-select:none}',
      '.tan-zm-lb.is-zoomed .tan-zm-lb__stage{cursor:grab}',
      '.tan-zm-lb.is-zoomed .tan-zm-lb__stage:active{cursor:grabbing}',
      '.tan-zm-lb__img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;transform-origin:center center;will-change:transform;-webkit-user-drag:none}',
      '.tan-zm-lb__nav{position:absolute;top:50%;z-index:2;transform:translateY(-50%);display:grid;place-items:center;width:44px;height:44px;border:1px solid #E8DCCB;border-radius:50%;background:rgb(255 255 255 / .92);color:inherit;cursor:pointer;box-shadow:0 4px 14px rgb(0 0 0 / .08);transition:opacity .2s}',
      '.tan-zm-lb__nav:hover{border-color:#5B3218;color:#5B3218}',
      '.tan-zm-lb__nav--prev{left:14px}.tan-zm-lb__nav--next{right:14px}',
      '.tan-zm-lb.is-zoomed .tan-zm-lb__nav{opacity:0;pointer-events:none}',
      '.tan-zm-lb__thumbs{display:flex;gap:8px;justify-content:center;overflow-x:auto;padding:10px 14px calc(10px + env(safe-area-inset-bottom));border-top:1px solid #E8DCCB;scrollbar-width:none}',
      '.tan-zm-lb__thumbs::-webkit-scrollbar{display:none}',
      '.tan-zm-lb__thumb{flex:0 0 auto;width:64px;height:64px;padding:0;border:1px solid #E8DCCB;border-radius:3px;background:#fff;overflow:hidden;cursor:pointer;opacity:.7;transition:opacity .2s,border-color .2s}',
      '.tan-zm-lb__thumb img{width:100%;height:100%;object-fit:contain;display:block}',
      '.tan-zm-lb__thumb:hover{opacity:1}',
      '.tan-zm-lb__thumb.is-active{opacity:1;border:2px solid #5B3218}',
      '.tan-zm-lb.is-single .tan-zm-lb__nav,.tan-zm-lb.is-single .tan-zm-lb__thumbs{display:none}',
      '.tan-zm-lb :focus-visible{outline:2px solid #5B3218;outline-offset:2px}',
      '@media (max-width:749px){.tan-zm-lb__nav{display:none}.tan-zm-lb__thumb{width:52px;height:52px}}',
      '@media (prefers-reduced-motion:reduce){.tan-zm-lb,.tan-zm-lb__img{transition:none!important}}'
    ].join('');
    var style = document.createElement('style');
    style.id = 'tan-zoom-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }
})();
